import { useMemo } from 'react';
import { type UseQueryResult, useMutation, useQueries } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import {
  getCustomerUser,
  getCustomerUsers
} from 'services/assist-api/entities/customer-user/customer-users';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import type { ResponseStatus } from 'services/assist-api/entities/test-instance-response/types';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import { getBatchedQueries } from 'services/assist-api/utils';
import {
  preparePatchTestInstanceUser,
  preparePostTestInstanceUser
} from './test-instance-users.prepare';

const CUSTOMER_USERS_BATCH_SIZE = 100;

type SmallestTestInstanceUser = API_JSON;
export type TestInstanceUserWithCustomerUserAndResponses = SmallestTestInstanceUser & {
  user: BaseCustomerUser;
  responses: API_JSON[];
};

export type BaseTestInstanceUser = API_JSON & {
  user: { id: number }; // This is the customer user, not the user itself.
  testInstance: { id: number };
  tokens?: { id: number; name: string; value: string }[];
  responses?: { id: number; status: ResponseStatus }[];
  level: API_JSON;
  referenceLevel: API_JSON;
};

export type BaseTestInstanceUserFull = BaseTestInstanceUser & { user: BaseCustomerUser };

const order = { 'order:[year]': 'desc', 'order:[id]': 'asc' };
const groups = [
  'userstestinstances-user',
  'userstestinstances-testinstance',
  'userstestinstances-tokens',
  'userstestinstances-responses',
  'userstestinstances-level',
  'userstestinstances-referencelevel'
];

const include = 'tokens,responses';

const getTestInstanceUsersNoDefaults: GetMany<SmallestTestInstanceUser> = async (params) =>
  (
    await ASSIST_API.get('users-test-instances', {
      params: requestParams({}, params)
    })
  ).data;

/**
 * Fetch users-test-instance
 * @param params can be used to customise/limit the users-test-instance retrieved
 * @returns the requested users-test-instance
 */
export const getTestInstanceUsers: GetMany<BaseTestInstanceUser> = async (params) =>
  (
    await ASSIST_API.get('users-test-instances', {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch users-test-instance with the related customer-user
 * @param params can be used to customise/limit the users-test-instance retrieved
 * @returns the requested users-test-instance and related customer-user
 */
export const getTestInstanceUsersFull: GetMany<BaseTestInstanceUserFull> = async (params) => {
  const users = await getTestInstanceUsers(params);

  // Todo: Cannot be returned as BaseTestInstanceUserFull[] or GetMany<BaseTestInstanceUserFull>. Needs refactoring.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (!users.meta?.totalItems) return users as RequestResponse<any[]>;

  const customerUsers = await getBatchedQueries(
    () => users?.json.length,
    async (itemsPerPage, page) =>
      (
        await getCustomerUsers({
          filters: {
            id: users?.json
              .slice(itemsPerPage * (page - 1), itemsPerPage * page)
              .map(({ user }) => user.id)
          }
        })
      ).json,
    CUSTOMER_USERS_BATCH_SIZE
  );

  return {
    ...users,
    json: users.json.map((data) => ({
      ...data,
      user: {
        ...data.user,
        ...customerUsers.find((custUser) => custUser.id === data.user.id)
      }
    }))
  };
};

/**
 * Fetch users-test-instance (singular)
 * @param params can be used to customise/limit the users-test-instance retrieved
 * @returns a single users-test-instance
 */
export const getTestInstanceUser: GetOne<BaseTestInstanceUser> = async (id, params) =>
  (
    await ASSIST_API.get(`users-test-instances/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Fetch users-test-instance with the related customer-user
 * @param params can be used to customise/limit the users-test-instance retrieved
 * @returns the requested users-test-instance and related customer-user
 */
export const getTestInstanceUserFull: GetOne<BaseTestInstanceUserFull> = async (id, params) => {
  const user = await getTestInstanceUser(id, params);

  if (!user.json) return user as RequestResponse<BaseTestInstanceUserFull>;

  const customerUser = await getCustomerUser(user?.json.user.id);

  return {
    ...user,
    json: {
      ...user.json,
      user: {
        ...user.json.user,
        ...customerUser.json
      }
    }
  };
};

export const useTestInstanceUsersNoDefaults: AssistQuery.UseMany<SmallestTestInstanceUser> = (
  params,
  options
) =>
  useQueryAssist(
    ['users-test-instances', params],
    () => getTestInstanceUsersNoDefaults(params),
    options
  );

/**
 * Hook implementation to fetch users-test-instance
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns users-test-instance matching request
 */
export const useTestInstanceUsers: AssistQuery.UseMany<BaseTestInstanceUser> = (params, options) =>
  useQueryAssist(['users-test-instances', params], () => getTestInstanceUsers(params), options);

/**
 * Hook implementation to fetch users-test-instance with related customer-user
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns users-test-instance matching request
 */
export const useTestInstanceUsersFull: AssistQuery.UseMany<BaseTestInstanceUserFull> = (
  params,
  options
) =>
  useQueryAssist(
    ['users-test-instances', 'full', params],
    () => getTestInstanceUsersFull(params),
    options
  );

/**
 * For some given testInstances, retrieve users-test-instances with their related customer-user.
 *  - splits queries for a lot of testInstances into parts
 *  - due to the above doesn't support pagination, or ordering
 */
export function useReportTestInstanceUsers(
  testInstanceIds: number[],
  options: {
    filters?: RequestParams['filters'];
    enabled: boolean;
  }
): {
  queries: UseQueryResult<RequestResponse<BaseTestInstanceUserFull[]>>[];
  data: BaseTestInstanceUserFull[] | undefined;
} {
  const testInstanceQueryLimit = 100;

  const batchedByTestInstance = Array.from({
    length: Math.ceil(testInstanceIds.length / testInstanceQueryLimit)
  }).map((_, i) => ({
    filters: {
      ...options?.filters,
      testInstance: testInstanceIds.slice(
        testInstanceQueryLimit * i,
        testInstanceQueryLimit * (i + 1)
      )
    }
  }));

  const queries = useQueries({
    queries: batchedByTestInstance.map((queryParams) => ({
      queryKey: ['users-test-instances', 'full', queryParams],
      queryFn: (): Promise<RequestResponse<BaseTestInstanceUserFull[]>> =>
        getTestInstanceUsersFull(queryParams),
      enabled: options.enabled
    }))
  });

  return useMemo(() => {
    // wait for all queries to succeed before creating data array
    if (queries.some((q) => !q.isSuccess)) return { queries, data: undefined };
    return { queries, data: queries.flatMap((q) => q.data?.json ?? []) };
  }, [queries]);
}

/**
 * Given a testInstanceUserId fetch and return the testInstanceUser
 *  - only runs once testInstanceUserId is provided
 *  - fetches the one testInstanceUser
 *  - caches data
 * @param id testInstanceUserId
 * @param params optional parameters to change requested data
 * @returns the matching testInstanceUser
 */
export const useTestInstanceUser: AssistQuery.UseOne<BaseTestInstanceUser> = (
  id,
  params,
  options
) =>
  useQueryAssist(
    ['users-test-instances', { id, ...params }],
    () => getTestInstanceUser(id, params),
    { ...options, enabled: !!id }
  );

/**
 * Hook implementation to fetch users-test-instance with related customer-user
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns users-test-instance matching request
 */
export const useTestInstanceUserFull: AssistQuery.UseOne<BaseTestInstanceUserFull> = (
  id,
  params,
  options
) =>
  useQueryAssist(
    ['users-test-instances', 'full', { id, ...params }],
    () => getTestInstanceUserFull(id, params),
    { ...options, enabled: !!id }
  );

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteTestInstanceUsers: AssistQuery.UseMutations<undefined, number[]> = (
  options
) =>
  useMutation(
    (ids: number[]) =>
      Promise.allSettled(ids.map((id) => ASSIST_API.delete(`users-test-instances/${id}`))),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['users-test-instances']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type PostTestInstanceUserVariables = {
  assessmentId: number;
  customerUserId: number;
  onBoardUUID?: string;
};

/**
 * Creates methods to send POST requests to the api
 *
 * mutate call will require a customer ID
 * so the testInstanceUser can be linked to the correct class
 *
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostTestInstanceUser: AssistQuery.UseMutation<
  BaseTestInstanceUser,
  PostTestInstanceUserVariables
> = (options) =>
  useMutation(
    ({ assessmentId, customerUserId, onBoardUUID }: PostTestInstanceUserVariables) =>
      ASSIST_API.post(
        'users-test-instances',
        preparePostTestInstanceUser(assessmentId, customerUserId, onBoardUUID),
        {
          params: requestParams({ order, groups, include })
        }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['users-test-instances']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type PostTestInstanceUsersVariables = { assessmentId: number; ids: number[] };

/**
 * Creates methods to send POST requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePostTestInstanceUsers: AssistQuery.UseMutations<
  undefined,
  PostTestInstanceUsersVariables
> = (options) =>
  useMutation(
    ({ assessmentId, ids }: PostTestInstanceUsersVariables) =>
      Promise.allSettled(
        ids.map((id) =>
          ASSIST_API.post('users-test-instances', preparePostTestInstanceUser(assessmentId, id), {
            params: requestParams({ order, groups, include })
          })
        )
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['users-test-instances']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

type PatchTestInstanceUsersVariables = Pick<BaseTestInstanceUser, 'id' | 'testInstance'>[];

/**
 * Creates methods to send PATCH requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const usePatchTestInstanceUsers: AssistQuery.UseMutations<
  undefined,
  PatchTestInstanceUsersVariables
> = (options) =>
  useMutation(
    (testInstanceUser: PatchTestInstanceUsersVariables) =>
      Promise.allSettled(
        testInstanceUser.map(({ id, testInstance }) =>
          ASSIST_API.patch(
            `users-test-instances/${id}`,
            preparePatchTestInstanceUser(testInstance.id, id),
            {
              params: requestParams({ order, groups, include })
            }
          )
        )
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['users-test-instances']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Given a customerUser and testInstance fetch and return the existence of the relation
 *  - caches data
 * @returns the boolean indicator
 */
export const fetchUserTestInstanceExists = (params, options): Promise<boolean> =>
  AssistQueryClient.fetchQuery(
    ['users-test-instances', { ...params }],
    async () =>
      !!(
        await ASSIST_API.get('users-test-instances', {
          params: requestParams(params)
        })
      ).data?.meta?.totalItems,
    options
  );

export const fetchUserTestInstanceRelationId = (
  user,
  testInstance,
  params?,
  options?
): Promise<unknown> =>
  AssistQueryClient.fetchQuery(
    ['users-test-instances', { ...params, filters: { user, testInstance } }],
    async () =>
      (
        await ASSIST_API.get('users-test-instances', {
          params: requestParams({ ...params, filters: { user, testInstance } })
        })
      ).data?.json[0]?.id,
    { ...options, enabled: !!user && !!testInstance }
  );
